import React from 'react'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import { noop, debounce } from 'lodash-es'
import { NegotiationDetail, EntityType, NegotiationActions, NegotiationEvent, UserPreference, AuditDetail } from 'api'
import { RFQCard } from 'view/shared/card'
import { RFQTicketHeader } from './RFQTicketHeader'
import { RFQTicketNarrativeBar } from './RFQTicketNarrativeBar'
import { RFQTicketFields } from './RFQTicketFields'
import { RFQTicketFooter } from './RFQTicketFooter'
import { RepositoryActions } from 'store/repository'
import { NegotiationFieldNames } from '../LiveNegotiationsGrid/LiveGridColumnNames'
import { sliceFieldName } from 'utils'
import { createUserPreferenceSelector } from 'selectors/userPreferenceSelectors'
import { isAuditDetail } from '../shared/utils'

interface RFQTicketContentProps {
  negotiationDetail: NegotiationDetail | AuditDetail
  userPreference: UserPreference
  showCloseButton?: boolean
  onClose?: () => unknown
  onUpdateField: typeof RepositoryActions.updateField
  onUpdateMeta: typeof RepositoryActions.updateMeta
  onLock: (negotiationId: string, ownOrder: boolean) => unknown
}

const mapStateToProps = createStructuredSelector({
  userPreference: createUserPreferenceSelector()
})

const mapDispatchToProps = {
  onUpdateField: RepositoryActions.updateField,
  onUpdateMeta: RepositoryActions.updateMeta,
  onLock: NegotiationActions.lock
}

export class RFQTicketContentComponent extends React.Component<RFQTicketContentProps> {
  debouncedLock: ReturnType<typeof debounce> | null = null

  componentDidMount() {
    this.debouncedLock = debounce(this.props.onLock, 500, { leading: true, trailing: false })
  }

  componentWillUnmount() {
    if (this.debouncedLock) {
      this.debouncedLock.cancel()
      this.debouncedLock = null
    }
  }

  render() {
    const { negotiationDetail, userPreference, showCloseButton, onClose } = this.props

    return (
      <>
        <RFQTicketHeader negotiationDetail={negotiationDetail} />
        <RFQCard negotiationDetail={negotiationDetail} userPreference={userPreference} inTicket={true} onClick={noop} />
        <RFQTicketFields
          negotiationDetail={negotiationDetail}
          userPreference={userPreference}
          onChangeField={this.handleChangeField}
          onUpdateField={this.handleUpdateField}
        />
        <RFQTicketNarrativeBar negotiationDetail={negotiationDetail} />
        <RFQTicketFooter negotiationDetail={negotiationDetail} showCloseButton={showCloseButton} onClose={onClose} />
      </>
    )
  }

  private handleChangeField = () => {
    const { negotiationDetail } = this.props

    if (isAuditDetail(negotiationDetail)) {
      return
    }

    const { negotiation, availableEvents } = negotiationDetail
    const { negotiationId, userIdWithLock } = negotiation

    if (this.debouncedLock && availableEvents.includes(NegotiationEvent.LOCK) && !userIdWithLock) {
      this.debouncedLock(negotiationId, negotiationDetail.ownOrder)
    }
  }

  private handleUpdateField = (value: any, field: string, rawValue?: any) => {
    const { negotiationDetail, onUpdateField, onUpdateMeta } = this.props
    const { negotiation } = negotiationDetail

    if (field === NegotiationFieldNames.OTW_DURATION) {
      onUpdateMeta(EntityType.NEGOTIATION, negotiation.negotiationId, field, value, {
        ownOrder: negotiationDetail.ownOrder
      })
    } else {
      const fieldName = sliceFieldName(field, 1)
      onUpdateField(EntityType.NEGOTIATION, negotiation.negotiationId, fieldName, value, rawValue, {
        ownOrder: negotiationDetail.ownOrder
      })
    }
  }
}

export const RFQTicketContent = connect(mapStateToProps, mapDispatchToProps)(RFQTicketContentComponent)
